// src/pages/DischargeLegal.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  Eye,
  FileText,
  Download,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  XCircle,
  RefreshCw,
  Building,
  User,
  CreditCard,
  DollarSign,
  Calendar,
  TrendingUp,
  Activity,
} from "lucide-react";

/**
 * Determines the next overall plot-level discharge status for Legal.
 * (For plot-level update after all receipts are approved.)
 */
const getNextStatus = (currentStatus, isApprove) => {
  if (!currentStatus) return "";
  if (currentStatus === "Submitted to Legal") {
    return isApprove ? "Approved Legal" : "Rejected Legal";
  }
  return "";
};

/**
 * Determines the expected receipt-level approval status for Legal,
 * based on the plot's discharge status.
 */
const getReceiptApprovalStatus = (plotDischarge, isApprove) => {
  if (plotDischarge === "Submitted to Legal") {
    return isApprove ? "LegalApproved" : "LegalRejected";
  }
  // Fallback
  return isApprove ? "LegalApproved" : "LegalRejected";
};

export default function DischargeLegal() {
  const [plots, setPlots] = useState([]);
  const [filteredPlots, setFilteredPlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("plot_number");
  const [sortOrder, setSortOrder] = useState("asc");

  // pagination for plots
  const [plotPage, setPlotPage] = useState(1);
  const plotsPerPage = 10;

  // Filter and search plots
  useEffect(() => {
    let filtered = [...plots];

    // Search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.plot_number.toLowerCase().includes(q) ||
          p.Customer_Name.toLowerCase().includes(q) ||
          p.Bank_Account.toLowerCase().includes(q)
      );
    }

    // Status filter - only show Legal submitted plots
    if (statusFilter !== "all") {
      filtered = filtered.filter((p) => p.discharge === statusFilter);
    } else {
      filtered = filtered.filter((p) => p.discharge === "Submitted to Legal");
    }

    // Sort filter
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case "plot_number":
          aVal = a.plot_number;
          bVal = b.plot_number;
          break;
        case "Customer_Name":
          aVal = a.Customer_Name;
          bVal = b.Customer_Name;
          break;
        case "purchase_price":
          aVal = parseFloat(a.purchase_price) || 0;
          bVal = parseFloat(b.purchase_price) || 0;
          break;
        default:
          return 0;
      }

      if (sortOrder === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredPlots(filtered);
    setPlotPage(1);
  }, [searchTerm, plots, statusFilter, sortBy, sortOrder]);

  const totalPlotPages = Math.ceil(filteredPlots.length / plotsPerPage);
  const displayedPlots = filteredPlots.slice(
    (plotPage - 1) * plotsPerPage,
    plotPage * plotsPerPage
  );

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlot, setModalPlot] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);

  // sales agreements state
  const [agreements, setAgreements] = useState([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);

  // pagination for receipts
  const [receiptsPage, setReceiptsPage] = useState(1);
  const receiptsPerPage = 10;
  const totalReceiptsPages = Math.ceil(receipts.length / receiptsPerPage);
  const displayedReceipts = receipts.slice(
    (receiptsPage - 1) * receiptsPerPage,
    receiptsPage * receiptsPerPage
  );

  useEffect(() => {
    axios
      .get("https://workspace.optiven.co.ke/api/mib/plots/discharge-pending")
      .then(({ data }) => {
        if (Array.isArray(data.plots)) {
          // Filter for Legal submitted plots only
          const legalSubmitted = data.plots.filter(
            (p) => p.discharge === "Submitted to Legal"
          );
          setPlots(legalSubmitted);
        } else setError("Unexpected data format.");
      })
      .catch(() => setError("Failed to load Legal discharge plots."))
      .finally(() => setLoading(false));
  }, []);

  const openModal = async (plot) => {
    setModalPlot(plot);
    setModalOpen(true);
    setReceipts([]);
    setAgreements([]);
    setReceiptsPage(1);
    setLoadingReceipts(true);
    setLoadingAgreements(true);

    // fetch receipts
    try {
      const { data } = await axios.get(
        `https://workspace.optiven.co.ke/api/mib/plots/${encodeURIComponent(
          plot.plot_number
        )}`
      );
      if (Array.isArray(data.receipts)) {
        setReceipts(data.receipts);
      } else {
        toast.error("No receipts found for this plot.");
      }
    } catch {
      toast.error("Could not load receipts for this plot.");
    } finally {
      setLoadingReceipts(false);
    }

    // fetch agreements
    try {
      const { data } = await axios.get(
        `https://workspace.optiven.co.ke/api/sla/plots/${encodeURIComponent(
          plot.plot_number
        )}/agreements`
      );
      if (Array.isArray(data)) {
        setAgreements(data);
      } else {
        toast.error("No sales agreements found.");
      }
    } catch {
      toast.error("Could not load sales agreements.");
    } finally {
      setLoadingAgreements(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalPlot(null);
    setReceipts([]);
    setAgreements([]);
  };

  const updateReceiptApproval = async (id, isApprove, comments) => {
    const status = getReceiptApprovalStatus(modalPlot.discharge, isApprove);
    try {
      await axios.patch(
        `https://workspace.optiven.co.ke/api/mib/receipts/${id}/approval`,
        { approval_status: status, approval_comments: comments }
      );
      toast.success(`Receipt ${id} → ${status}`);
      setReceipts((r) =>
        r.map((x) =>
          x.id === id
            ? { ...x, approval_status: status, approval_comments: comments }
            : x
        )
      );
    } catch {
      toast.error("Failed to update receipt.");
    }
  };

  const confirmDischarge = async () => {
    const expected = getReceiptApprovalStatus(modalPlot.discharge, true);
    const allOK = receipts.every((r) => r.approval_status === expected);
    if (!allOK) {
      toast.error("All receipts must be approved at Legal level.");
      return;
    }
    const next = getNextStatus(modalPlot.discharge, true);
    if (!next) {
      toast.error("Cannot determine next status.");
      return;
    }
    try {
      await axios.patch(
        `https://workspace.optiven.co.ke/api/mib/plots/${encodeURIComponent(
          modalPlot.plot_number
        )}/discharge`,
        { discharge: next }
      );
      toast.success(`Plot → ${next}`);
      setPlots((p) =>
        p.map((x) =>
          x.plot_number === modalPlot.plot_number
            ? { ...x, discharge: next }
            : x
        )
      );
      closeModal();
    } catch {
      toast.error("Failed to update plot.");
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-lg font-medium text-gray-700">
              Loading Legal discharge plots...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please wait while we fetch the latest data
            </p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              Error Loading Plots
            </h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Legal Discharge Processing
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Final legal review and approval of discharge plots
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Plots</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {plots.length}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {filteredPlots.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search by plot number, customer, or bank..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="Submitted to Legal">Submitted to Legal</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="plot_number">Sort by Plot</option>
                  <option value="Customer_Name">Sort by Customer</option>
                  <option value="purchase_price">Sort by Price</option>
                </select>
                <button
                  onClick={() =>
                    setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                  }
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {sortOrder === "asc" ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Legal Processing Pipeline ({filteredPlots.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plot Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bank Account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedPlots.map((plot, i) => {
                    const idx = (plotPage - 1) * plotsPerPage + i + 1;
                    return (
                      <tr key={plot.plot_number} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <Building className="h-5 w-5 text-purple-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {plot.plot_number}
                              </div>
                              <div className="text-sm text-gray-500">
                                Plot #{idx}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {plot.Customer_Name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {plot.Bank_Account}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {plot.purchase_price
                              ? `Ksh. ${parseFloat(
                                  plot.purchase_price
                                ).toLocaleString()}`
                              : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            {plot.discharge}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => openModal(plot)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Legal Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPlotPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPlotPage((p) => p - 1)}
                  disabled={plotPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPlotPage((p) => p + 1)}
                  disabled={plotPage === totalPlotPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(plotPage - 1) * plotsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(plotPage * plotsPerPage, filteredPlots.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">{filteredPlots.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPlotPage((p) => p - 1)}
                      disabled={plotPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronDown className="h-5 w-5 transform rotate-90" />
                    </button>
                    {Array.from(
                      { length: totalPlotPages },
                      (_, i) => i + 1
                    ).map((page) => (
                      <button
                        key={page}
                        onClick={() => setPlotPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === plotPage
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setPlotPage((p) => p + 1)}
                      disabled={plotPage === totalPlotPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronDown className="h-5 w-5 transform -rotate-90" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RECEIPTS & AGREEMENTS MODAL */}
        {modalOpen && modalPlot && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={closeModal}
              ></div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                <div className="bg-white">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Building className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">
                          Plot {modalPlot.plot_number} - Legal Review
                        </h3>
                        <p className="text-sm text-gray-500">
                          Final legal review and approval
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6">
                    {/* Plot Information */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="text-sm font-medium text-gray-900 font-medium mb-3">
                        Plot Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Customer Name</p>
                          <p className="text-sm font-medium text-gray-900">
                            {modalPlot.Customer_Name}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">
                            Current Status
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {modalPlot.discharge}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Bank Account</p>
                          <p className="text-sm font-medium text-gray-900">
                            {modalPlot.Bank_Account}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Sales Agreements */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-gray-900">
                          Sales Agreements
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {agreements.length} files
                        </span>
                      </div>
                      {loadingAgreements ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-gray-600">
                            Loading agreements...
                          </span>
                        </div>
                      ) : agreements.length > 0 ? (
                        <div className="space-y-3">
                          {agreements.map((a) => (
                            <div
                              key={a.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                    <Download className="h-4 w-4 text-green-600" />
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900">
                                    {a.original_name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Uploaded{" "}
                                    {new Date(
                                      a.uploaded_at
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <a
                                href={`https://workspace.optiven.co.ke/api/sla/plots/${modalPlot.plot_number}/agreements/${a.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <Download className="h-3 w-3 mr-1" />
                                Download
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No agreements found
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            No sales agreements have been uploaded for this
                            plot.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Receipts */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-medium text-gray-900">
                          Payment Receipts - Legal Review
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {receipts.length} receipts
                        </span>
                      </div>
                      {loadingReceipts ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="ml-2 text-gray-600">
                            Loading receipts...
                          </span>
                        </div>
                      ) : receipts.length === 0 ? (
                        <div className="text-center py-8">
                          <FileText className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No receipts found
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            No payment receipts have been found for this plot.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Receipt ID
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Narration
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Reassign Reason
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {displayedReceipts.map((r) => (
                                  <ReceiptRow
                                    key={r.id}
                                    receipt={r}
                                    onUpdate={updateReceiptApproval}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {totalReceiptsPages > 1 && (
                            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
                              <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                  onClick={() => setReceiptsPage((p) => p - 1)}
                                  disabled={receiptsPage === 1}
                                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                  Previous
                                </button>
                                <button
                                  onClick={() => setReceiptsPage((p) => p + 1)}
                                  disabled={receiptsPage === totalReceiptsPages}
                                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                  Next
                                </button>
                              </div>
                              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                  <p className="text-sm text-gray-700">
                                    Showing page{" "}
                                    <span className="font-medium">
                                      {receiptsPage}
                                    </span>{" "}
                                    of{" "}
                                    <span className="font-medium">
                                      {totalReceiptsPages}
                                    </span>
                                  </p>
                                </div>
                                <div>
                                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                      onClick={() =>
                                        setReceiptsPage((p) => p - 1)
                                      }
                                      disabled={receiptsPage === 1}
                                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                      <ChevronDown className="h-5 w-5 transform rotate-90" />
                                    </button>
                                    <button
                                      onClick={() =>
                                        setReceiptsPage((p) => p + 1)
                                      }
                                      disabled={
                                        receiptsPage === totalReceiptsPages
                                      }
                                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                      <ChevronDown className="h-5 w-5 transform -rotate-90" />
                                    </button>
                                  </nav>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
                    <button
                      onClick={closeModal}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Close
                    </button>
                    <button
                      onClick={confirmDischarge}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Legal Approval
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer position="top-right" />
    </Sidebar>
  );
}

function ReceiptRow({ receipt, onUpdate }) {
  const [comments, setComments] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleApprove = async () => {
    setIsProcessing(true);
    await onUpdate(receipt.id, true, comments);
    setIsProcessing(false);
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      toast.error("Please enter a reason for rejection");
      return;
    }
    setIsProcessing(true);
    await onUpdate(receipt.id, false, comments);
    setIsProcessing(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Level1Approved":
      case "Level2Approved":
      case "LegalApproved":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case "Level1Rejected":
      case "Level2Rejected":
      case "LegalRejected":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        #{receipt.id}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        <div className="max-w-xs truncate" title={receipt.Narration}>
          {receipt.Narration}
        </div>
        {receipt.reassign_comment && (
          <div className="text-xs text-gray-500 mt-1">
            Reassign: {receipt.reassign_comment}
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-sm text-gray-900">
        {receipt.reassign_comment || "—"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        Ksh. {parseFloat(receipt.Amount_LCY || 0).toLocaleString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {getStatusBadge(receipt.approval_status)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="space-y-3">
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter comments..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={2}
          />
          <div className="flex space-x-2">
            <button
              onClick={handleApprove}
              disabled={isProcessing}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
              ) : (
                <Check className="h-3 w-3 mr-1" />
              )}
              Approve
            </button>
            <button
              onClick={handleReject}
              disabled={isProcessing}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
              ) : (
                <X className="h-3 w-3 mr-1" />
              )}
              Reject
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}
